import React, { useEffect, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { ArrowRight, BookOpen, User } from 'lucide-react';
import { fetchSubjectCourses, SubjectCourseDto } from '../services/gradeApi';
import { handleCourseImageError, resolveCourseImageUrl } from '../utils/courseImage';
import { getTermInArabic } from '../utils/gradeTranslations';

type LocationState = {
  subjectName?: string;
  gradeId?: string;
  gradeTitle?: string;
  gradeCode?: string;
};

const formatPrice = (n: number) => (n === 0 ? 'مجاناً' : `${n.toLocaleString('ar-EG')} ج.م`);

const SubjectCoursesPage: React.FC = () => {
  const { subjectId } = useParams<{ subjectId: string }>();
  const location = useLocation();
  const nav = (location.state as LocationState) || {};

  const subjectName = nav.subjectName || 'المادة';
  const gradeId = nav.gradeId;
  const gradeTitle = nav.gradeTitle || 'المرحلة';

  const [courses, setCourses] = useState<SubjectCourseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!subjectId) {
      setError('معرّف المادة غير صالح');
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const result = await fetchSubjectCourses(subjectId);
      if (cancelled) return;
      if (!result.ok) {
        setError(result.message || 'فشل تحميل المناهج');
        setCourses([]);
      } else {
        setCourses(result.courses);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [subjectId]);

  const subjectsListPath = gradeId ? `/grades/${encodeURIComponent(gradeId)}/subjects` : '/';
  const subjectsListState =
    gradeId != null
      ? { gradeTitle: nav.gradeTitle, gradeCode: nav.gradeCode }
      : undefined;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-emerald-50/30 to-slate-50" dir="rtl">
      <div className="border-b border-emerald-100 bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-slate-500 mb-4" dir="ltr">
            <Link to="/" className="hover:text-emerald-600 transition-colors">
              الرئيسية
            </Link>
            <ArrowRight className="w-4 h-4 text-slate-400 rotate-180" aria-hidden />
            {gradeId ? (
              <>
                <Link
                  to={subjectsListPath}
                  state={subjectsListState}
                  className="hover:text-emerald-600 transition-colors max-w-[10rem] truncate sm:max-w-none"
                  title={gradeTitle}
                >
                  مواد {gradeTitle}
                </Link>
                <ArrowRight className="w-4 h-4 text-slate-400 rotate-180" aria-hidden />
              </>
            ) : null}
            <span className="text-slate-800 font-medium text-right truncate max-w-[12rem] sm:max-w-md" dir="rtl">
              مناهج {subjectName}
            </span>
          </nav>
          <div>
            <h1 className="font-el-messiri text-2xl sm:text-3xl font-bold text-slate-900">
              مناهج <span className="text-emerald-700">{subjectName}</span>
            </h1>
            <p className="font-cairo text-slate-600 mt-2 text-sm">المناهج المرتبطة بهذه المادة من المنصة</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading && (
          <div className="text-center py-16 font-cairo text-slate-500">جاري تحميل المناهج...</div>
        )}

        {!loading && error && (
          <div className="max-w-lg mx-auto text-center rounded-2xl border border-red-100 bg-red-50/80 px-6 py-8">
            <p className="font-cairo text-red-800 text-sm">{error}</p>
            <Link
              to={gradeId ? subjectsListPath : '/'}
              state={subjectsListState}
              className="mt-4 inline-block font-cairo text-sm text-emerald-700 font-semibold hover:underline"
            >
              العودة
            </Link>
          </div>
        )}

        {!loading && !error && courses.length === 0 && (
          <div className="text-center py-16 rounded-2xl border border-emerald-100 bg-white shadow-sm">
            <BookOpen className="h-12 w-12 text-emerald-200 mx-auto mb-3" aria-hidden />
            <p className="font-cairo text-slate-600">لا توجد مناهج مفعّلة لهذه المادة حالياً</p>
            <Link
              to={subjectsListPath}
              state={subjectsListState}
              className="mt-6 inline-block font-cairo text-sm text-emerald-700 font-semibold hover:underline"
            >
              العودة إلى المواد
            </Link>
          </div>
        )}

        {!loading && !error && courses.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((c) => (
              <Link
                key={c.id}
                to={`/courses/${c.id}`}
                className="group flex flex-col overflow-hidden rounded-2xl border border-emerald-100 bg-white shadow-sm transition hover:border-emerald-300 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              >
                <div className="relative aspect-[16/10] bg-slate-100 overflow-hidden">
                  <img
                    src={resolveCourseImageUrl(c.imagePath)}
                    alt=""
                    className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                    loading="lazy"
                    onError={handleCourseImageError}
                  />
                </div>
                <div className="flex flex-1 flex-col p-4 text-right">
                  <h2 className="font-el-messiri text-lg font-bold text-slate-900 leading-snug group-hover:text-emerald-800 transition-colors">
                    {c.courseName}
                  </h2>
                  {c.description ? (
                    <p className="font-cairo mt-2 text-sm text-slate-600 line-clamp-2 flex-1">{c.description}</p>
                  ) : (
                    <div className="flex-1 min-h-[2rem]" />
                  )}
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-emerald-50 pt-3">
                    <span className="font-cairo text-sm font-bold text-emerald-800">{formatPrice(c.price)}</span>
                    {c.term ? (
                      <span className="font-cairo text-xs text-slate-500 rounded-full bg-slate-100 px-2 py-0.5">
                        {getTermInArabic(c.term)}
                      </span>
                    ) : null}
                  </div>
                  {c.teacherName ? (
                    <p className="font-cairo mt-2 flex items-center justify-end gap-1.5 text-xs text-slate-500">
                      <User className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      {c.teacherName}
                    </p>
                  ) : null}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubjectCoursesPage;
